import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, Folder, Paperclip } from "lucide-react";
import { Link } from "react-router-dom";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { formatTaipeiDateTime } from "../../lib/datetime";
import { mailbox, mailErrorMessage } from "../../api/mailbox";
import type { MailAttachment, SystemSentDetail, SystemSentItem } from "../../api/types";
import { AttachmentPreview } from "./AttachmentPreview";
import { MailSourceTools } from "./MailSourceTools";
import { buildSrcDoc, formatSize } from "./MailReader";

// 「系統寄出」(信箱左欄,業主 2026-10-05):系統替本人寄的信 —— 報價單、回簽信 —— 與有沒有寄成功。
//
// - 只有本人的(後端以登入者篩,老闆也一樣);沒有刪除 / 搬移 / 星號 / 批次,純讀。
// - 讀信沿用信箱的:HTML 後端已清洗、放 sandbox iframe;附件預覽 / 下載、列印、原始碼、.eml。
// - 讀信區最上面是狀態列:「已寄出 2026/10/05 14:03」或「寄送失敗:原因」;信是從報價單寄的就有連結過去。
// - 舊紀錄(這個功能之前寄的)沒有原信:顯示當時記下的收件者、主旨、內文與附件檔名,標「舊紀錄,無原信」。
// - **不需要啟用個人信箱**(也不碰 IMAP / 金鑰):沒啟用的人從信箱頁照樣開得了。

export type MobilePane = "folders" | "list";

export interface SystemSentPanelProps {
  /** 手機單欄流程:目前頁面停在哪一欄(資料夾 / 列表)。 */
  mobilePane: MobilePane;
  onShowFolders: () => void;
}

function statusLine(d: SystemSentDetail): string {
  if (d.status === "SENT") return `已寄出 ${formatTaipeiDateTime(d.sent_at ?? d.date)}`.trim();
  if (d.status === "FAILED") return `寄送失敗:${d.error || "沒有留下原因"}`;
  return "還沒寄出(寄送中,或寄到一半中斷了)";
}

const STATUS_TONE = {
  SENT: "ok",
  FAILED: "danger",
  QUEUED: "neutral",
} as const;
const STATUS_TEXT = {
  SENT: "已寄出",
  FAILED: "寄送失敗",
  QUEUED: "待寄出",
} as const;

export function SystemSentPanel({ mobilePane, onShowFolders }: SystemSentPanelProps) {
  const [items, setItems] = useState<SystemSentItem[]>([]);
  const [nextBefore, setNextBefore] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [detail, setDetail] = useState<SystemSentDetail | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);

  const alive = useRef(true);
  const openRef = useRef<number | null>(null);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(async (before: number | null = null) => {
    setLoading(true);
    try {
      const got = await mailbox.systemSent(before);
      if (!alive.current) return;
      const rows = Array.isArray(got?.items) ? got.items : [];
      setItems((prev) => (before === null ? rows : [...prev, ...rows]));
      setNextBefore(got?.next_before ?? null);
      setError(null);
    } catch (err) {
      if (alive.current) setError(mailErrorMessage(err, "讀不到系統寄出的信,請稍後再試"));
    } finally {
      if (alive.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const open = async (one: SystemSentItem) => {
    openRef.current = one.id;
    setOpenId(one.id);
    setDetail(null);
    setDetailError(null);
    try {
      const got = await mailbox.systemSentOne(one.id);
      if (alive.current && openRef.current === one.id) setDetail(got);
    } catch (err) {
      if (alive.current && openRef.current === one.id) setDetailError(mailErrorMessage(err, "讀不到這封信"));
    }
  };

  const close = () => {
    openRef.current = null;
    setOpenId(null);
    setDetail(null);
    setDetailError(null);
  };

  const showReader = openId !== null;
  return (
    <>
      <div
        data-mail-pane="list"
        className={
          "min-w-0 flex-col gap-2 lg:min-h-0 lg:w-[28rem] lg:shrink-0 lg:border-r lg:border-[var(--border)] lg:px-3 2xl:w-[32rem] " +
          (!showReader && mobilePane === "list" ? "flex" : "hidden md:flex")
        }
      >
        <div className="flex items-center gap-2 md:hidden">
          <Button
            size="sm"
            variant="ghost"
            data-pane-start=""
            icon={<Folder aria-hidden="true" size={16} />}
            onClick={onShowFolders}
          >
            資料夾
          </Button>
          <span className="min-w-0 flex-1 text-sm font-medium text-[var(--text)]">系統寄出</span>
        </div>
        <p className="m-0 text-xs text-[var(--text-3)]">系統替你寄出的信(報價單、回簽信),只有你自己看得到,只能讀。</p>
        <div data-mail-scroll="" className="min-h-0 flex-1 overflow-y-auto">
          {error ? (
            <div className="flex flex-wrap items-center gap-2">
              <p role="alert" className="m-0 text-sm text-[var(--danger)]">
                {error}
              </p>
              <Button size="sm" onClick={() => void load()}>
                再試一次
              </Button>
            </div>
          ) : !loading && items.length === 0 ? (
            <p className="m-0 p-2 text-sm text-[var(--text-3)]">還沒有系統替你寄出的信。</p>
          ) : (
            <ul aria-label="系統寄出清單" className="m-0 flex flex-col gap-1 p-0">
              {items.map((one) => (
                <li key={one.id} className="list-none">
                  <button
                    type="button"
                    data-mail-row={one.id}
                    aria-current={openId === one.id ? "true" : undefined}
                    onClick={() => void open(one)}
                    className={
                      "flex w-full min-w-0 flex-col gap-0.5 rounded-[var(--radius)] border px-3 py-2 text-left " +
                      (openId === one.id
                        ? "border-[var(--accent)] bg-[var(--accent-soft)]"
                        : "border-[var(--border)] bg-[var(--surface)] hover:bg-[var(--surface-2)]")
                    }
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="min-w-0 flex-1 truncate text-sm text-[var(--text)]">
                        給 {one.to_addrs.join("、") || "(沒有收件人)"}
                      </span>
                      <span className="shrink-0 text-xs tabular text-[var(--text-3)]">
                        {formatTaipeiDateTime(one.date)}
                      </span>
                    </span>
                    <span className="flex min-w-0 flex-wrap items-center gap-1.5">
                      <Badge tone={STATUS_TONE[one.status] ?? "neutral"}>{STATUS_TEXT[one.status] ?? one.status}</Badge>
                      {one.has_attachments ? (
                        <span className="inline-flex text-[var(--text-3)]">
                          <Paperclip aria-hidden="true" size={14} />
                          <span className="sr-only">有附件</span>
                        </span>
                      ) : null}
                      <span className="min-w-0 flex-1 truncate text-sm text-[var(--text-2)]">
                        {one.subject || "(無主旨)"}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {loading ? <p className="m-0 p-2 text-sm text-[var(--text-3)]">載入中…</p> : null}
          {nextBefore !== null && !loading ? (
            <Button size="sm" variant="ghost" onClick={() => void load(nextBefore)}>
              載入更多
            </Button>
          ) : null}
        </div>
      </div>

      <div
        data-mail-pane="reader"
        className={"min-w-0 flex-1 lg:min-h-0 lg:overflow-y-auto lg:pl-3 " + (showReader ? "" : "hidden md:block")}
      >
        {detail && detail.id === openId ? (
          <SystemSentReader detail={detail} onBack={close} />
        ) : detailError ? (
          <div className="flex items-center justify-between gap-2">
            <p role="alert" className="m-0 text-sm text-[var(--danger)]">
              {detailError}
            </p>
            <Button
              size="sm"
              variant="ghost"
              data-back=""
              icon={<ArrowLeft aria-hidden="true" size={16} />}
              onClick={close}
            >
              回列表
            </Button>
          </div>
        ) : openId !== null ? (
          <div className="flex items-center justify-between gap-2">
            <p className="m-0 text-sm text-[var(--text-3)]">載入中…</p>
            <Button
              size="sm"
              variant="ghost"
              data-back=""
              icon={<ArrowLeft aria-hidden="true" size={16} />}
              onClick={close}
            >
              回列表
            </Button>
          </div>
        ) : (
          <p className="m-0 text-sm text-[var(--text-3)]">點左邊的信來讀。</p>
        )}
      </div>
    </>
  );
}

function SystemSentReader({ detail, onBack }: { detail: SystemSentDetail; onBack: () => void }) {
  const srcDoc = useMemo(() => (detail.html ? buildSrcDoc(detail.html, false) : ""), [detail.html]);
  const subject = detail.subject || "(無主旨)";
  const failed = detail.status === "FAILED";
  return (
    <article
      aria-label={`系統寄出:${subject}`}
      className="nq-mail-print flex min-w-0 flex-col gap-3 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] p-4"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h3 className="m-0 min-w-0 flex-1 text-lg font-semibold text-[var(--text)]">{subject}</h3>
        <Button
          size="sm"
          variant="ghost"
          icon={<ArrowLeft aria-hidden="true" size={16} />}
          onClick={onBack}
          data-back=""
          className="print:hidden"
        >
          回列表
        </Button>
      </div>

      <p
        role="status"
        aria-label="寄送狀態"
        className={
          "m-0 text-sm font-medium " +
          (failed ? "text-[var(--danger)]" : detail.status === "SENT" ? "text-[var(--ok)]" : "text-[var(--text-2)]")
        }
      >
        {statusLine(detail)}
      </p>
      {detail.quote_id ? (
        <p className="m-0 text-sm print:hidden">
          <Link
            to={`/quotes/${detail.quote_id}`}
            className="text-[var(--accent-hover)] underline-offset-2 hover:underline"
          >
            報價單 {detail.quote_no || `#${detail.quote_id}`}
          </Link>
        </p>
      ) : null}
      {detail.legacy ? (
        <p className="m-0 flex flex-wrap items-center gap-2 text-sm text-[var(--text-3)]">
          <Badge tone="neutral">舊紀錄,無原信</Badge>
          {detail.raw_missing
            ? "原信的備份檔找不到了;這裡只有當時記下的內容。"
            : "這是系統開始保存原信之前寄的;這裡只有當時記下的收件者、主旨、內文與附件檔名。"}
        </p>
      ) : null}

      <div className="flex flex-col gap-0.5 text-sm text-[var(--text-2)]">
        <span>
          <span className="text-[var(--text-3)]">寄件人:</span>
          {detail.from_addr}
          {detail.on_behalf_of ? `(代 ${detail.on_behalf_of} 寄出)` : ""}
        </span>
        <span>
          <span className="text-[var(--text-3)]">收件人:</span>
          {detail.to_addrs.join("、")}
        </span>
        {detail.cc_addrs.length > 0 ? (
          <span>
            <span className="text-[var(--text-3)]">副本:</span>
            {detail.cc_addrs.join("、")}
          </span>
        ) : null}
      </div>

      {detail.legacy ? null : <MailSourceTools messageId={detail.id} scope="system-sent" />}

      {detail.html ? (
        <iframe
          title="信件內容"
          sandbox="allow-popups allow-popups-to-escape-sandbox"
          srcDoc={srcDoc}
          referrerPolicy="no-referrer"
          className="h-[60vh] min-h-64 w-full rounded-[var(--radius)] border border-[var(--border)]"
        />
      ) : detail.text ? (
        <pre className="m-0 whitespace-pre-wrap break-words font-sans text-sm text-[var(--text)]">{detail.text}</pre>
      ) : (
        <p className="m-0 text-sm text-[var(--text-3)]">(這封信沒有內文)</p>
      )}

      {detail.legacy ? (
        detail.attachments.length > 0 ? (
          <section aria-label="附件" className="flex flex-col gap-1">
            <h4 className="m-0 text-sm font-medium text-[var(--text-2)]">附件(只有檔名,沒有留存檔案)</h4>
            <ul className="m-0 flex flex-col gap-1 p-0">
              {detail.attachments.map((one, i) => (
                <li key={i} className="flex list-none items-center gap-1.5 text-sm text-[var(--text-2)]">
                  <Paperclip aria-hidden="true" size={14} />
                  {one.filename || "(沒有檔名)"}
                  <span className="text-xs text-[var(--text-3)]">({formatSize(one.size)})</span>
                </li>
              ))}
            </ul>
          </section>
        ) : null
      ) : (
        <AttachmentPreview
          messageId={detail.id}
          attachments={detail.attachments as MailAttachment[]}
          scope="system-sent"
        />
      )}
    </article>
  );
}
